import { sql } from 'drizzle-orm';
import {
  boolean,
  index,
  integer,
  numeric,
  pgTable,
  text,
  timestamp,
  uuid,
} from 'drizzle-orm/pg-core';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { z } from 'zod';
import { appStatusEnum, appTypeEnum, criticalityEnum, visibilityEnum } from './enums.ts';
import { APPLICATION_MAX_IMAGES, applicationImageKeySchema } from './uploads.ts';

/**
 * Tabela `applications` — catálogo de aplicações da Masterboi.
 * Campos derivados do mock de design (data.jsx). Nenhum documento é hospedado
 * aqui — apenas links externos que abrem em nova aba.
 *
 * Campos nullable (T | null no TS): urlHml, urlDev, ticket, project,
 * requirements, docsTechnical, docsBusiness, docsApi, repo, imageKey,
 * deployedAt, todo o bloco `projeto*` e o bloco `acessos*` (coletado do
 * Clerk). O frontend trata explicitamente (DocLinkCard mostra "Não
 * cadastrado"; sem `imageKey` o catálogo cai no placeholder sintético).
 */
export const applications = pgTable(
  'applications',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    /**
     * Código sequencial LEGÍVEL da aplicação — o que a interface mostra e o que
     * se cita em chamado/auditoria (via `formatAppCode`: `APP-007`). O UUID
     * continua sendo a chave técnica (rotas, FKs), mas não aparece para gente.
     * GENERATED ALWAYS: o banco numera sozinho (inclusive o backfill das linhas
     * existentes na migration) e recusa escrita manual — sem colisão possível.
     */
    code: integer('code').generatedAlwaysAsIdentity().notNull().unique(),
    name: text('name').notNull(),
    slug: text('slug').notNull().unique(),
    type: appTypeEnum('type').notNull(),
    visibility: visibilityEnum('visibility').notNull().default('publico'),
    // Confidencialidade por colaborador: quando true o acesso é restrito às
    // matrículas de `application_collaborators` (schemas/application-access.ts),
    // além das unidades/setores. Toggle da tela de cadastro.
    restrictedByCollaborator: boolean('restricted_by_collaborator').notNull().default(false),
    status: appStatusEnum('status').notNull().default('prod'),
    // Apresentação do ícone/avatar (cor da marca + letra).
    iconBg: text('icon_bg').notNull().default('#C8102E'),
    iconLetter: text('icon_letter').notNull().default('A'),
    /**
     * Imagem/screenshot da aplicação (card do catálogo e capa da tela de
     * detalhe). Guarda SÓ A CHAVE DO OBJETO no bucket S3/MinIO (ex.:
     * `applications/<uuid>.webp`) — nunca uma URL absoluta, para o registro não
     * amarrar endpoint/bucket/ambiente (dev, UAT e PRD usam buckets distintos).
     * A imagem é servida pela própria API em
     * `GET /api/uploads/application-image/:key`; o front monta a URL a partir da
     * chave. `null` = sem imagem → placeholder sintético.
     */
    imageKey: text('image_key'),
    /**
     * Telas ADICIONAIS da aplicação, na ordem em que o apresentador vai
     * mostrá-las. A CAPA continua sendo `imageKey`, sozinha e intocada — é o que
     * o catálogo, a ficha e a capa do slide já leem, e migrar isso para dentro
     * de um array significaria reescrever todo consumidor de imagem por uma
     * mudança que é de APRESENTAÇÃO, não de identidade da aplicação.
     *
     * `text[]` copiando o precedente de `stacks` (acima): a casa já tem essa
     * forma, já sabe gerar a migration dela e já a serializa no DTO. Tabela
     * filha daria legenda por imagem e FK, que ninguém pediu; jsonb daria
     * tipagem frouxa numa coluna cujo conteúdo é uma lista de chaves validadas
     * por regex.
     *
     * A galeria NÃO inclui a capa: `[capa, ...galeriaKeys]` é a sequência
     * completa do carrossel, e o teto de APPLICATION_MAX_IMAGES conta o conjunto.
     */
    galeriaKeys: text('galeria_keys').array().notNull().default(sql`'{}'::text[]`),
    /**
     * "Desabilitar apresentação": a aplicação NÃO entra no deck nem no PDF da
     * tela de apresentação. É UMA coluna vista de dois lugares — o check da
     * ficha (cadastro/edição) e a lista "Plataformas" do lobby, onde desmarcar
     * grava aqui — para os dois nunca discordarem.
     *
     * Persistida, e não memória da tela do lobby como era, por pedido explícito:
     * o que se desmarca no lobby tem de sumir da apresentação DE VEZ, e o mesmo
     * check tem de aparecer na edição da ficha. `notNull().default(false)` como
     * os toggles de conformidade: ausente no payload = apresenta.
     *
     * Sem `.default()` no Zod de propósito — o default é do BANCO. Um default
     * no Zod faria qualquer PATCH de outro campo (`{ owner }`) devolver a coluna
     * para `false` em silêncio, apagando a escolha feita no lobby.
     */
    apresentacaoDesabilitada: boolean('apresentacao_desabilitada').notNull().default(false),
    description: text('description').notNull(),
    // URLs por ambiente — geram os cards do catálogo. TODAS nullable: qual URL
    // é obrigatória depende da SITUAÇÃO (missingStatusUrl, no fim do arquivo) —
    // produção exige `url`, homologação exige `urlHml`; uma aplicação em dev
    // pode ainda não ter nenhum ambiente publicado.
    url: text('url'),
    urlHml: text('url_hml'),
    urlDev: text('url_dev'),
    /**
     * @deprecated COLUNA MORTA — favorito virou POR USUÁRIO (`user_favorites`).
     *
     * Ninguém mais lê nem escreve esta coluna: ela está fora de
     * `applicationCreateSchema` (a API recusa o campo no POST/PATCH), fora do
     * seed, e o `favorite` do DTO é calculado a partir de `user_favorites` do
     * usuário logado (routes/applications.ts). O valor gravado aqui NUNCA chega
     * ao payload — `toApplicationDTO` desestrutura a coluna para fora antes de
     * montar a resposta.
     *
     * Por que não DROP COLUMN na 0004: `apps/web/src/lib/catalog-config.ts`
     * define `App = Omit<Application, 'createdAt' | 'updatedAt'> & {...}` — o
     * tipo do FRONT sai de `$inferSelect`, não do `ApplicationDTO`. Remover a
     * coluna apagaria `favorite` de `App` e quebraria o typecheck de
     * `apps/web` (catalog-screen, favorites-page) e de
     * `apps/api/tests/compliance.test.ts` (fixture `EMPTY: App`). O DROP entra
     * numa migration futura, junto com a troca de `App` para `ApplicationDTO`.
     */
    favorite: boolean('favorite').notNull().default(false),
    // Rastreabilidade (auditoria KPMG).
    ticket: text('ticket'),
    project: text('project'),
    // Documentação — apenas links.
    requirements: text('requirements'),
    docsTechnical: text('docs_technical'),
    docsBusiness: text('docs_business'),
    docsApi: text('docs_api'),
    repo: text('repo'),
    // Stack técnico.
    stacks: text('stacks').array().notNull().default(sql`'{}'::text[]`),
    owner: text('owner').notNull(),
    team: text('team').notNull(),
    criticality: criticalityEnum('criticality').notNull().default('media'),
    // ── Conformidade (seção "LGPD e conformidade" do cadastro) ───────────────
    // Uma coluna por toggle do formulário; todas `notNull().default(false)`
    // (ausente no payload = "não" declarado, nunca desconhecido).
    // `lgpd`             → "Trata dados pessoais (LGPD)" · DPIA exigido · DPO
    lgpd: boolean('lgpd').notNull().default(false),
    // `dadosSensiveis`   → "Possui dados sensíveis (art. 5º LGPD)" · saúde,
    //                       biometria, etnia, religião…
    dadosSensiveis: boolean('dados_sensiveis').notNull().default(false),
    // `integracaoExterna`→ "Possui integração com sistemas externos" · bancos,
    //                       governo, parceiros, SaaS
    integracaoExterna: boolean('integracao_externa').notNull().default(false),
    // `usoIa`            → "Faz uso de IA / modelos generativos" · inventário
    //                       de IA · ISO 42001
    usoIa: boolean('uso_ia').notNull().default(false),
    // ── Projeto (seção "Projeto" do cadastro) ────────────────────────────────
    // Bloco do ponto de vista de QUEM PEDIU a aplicação, não de quem a
    // constrói: esforço gasto, retorno esperado, área solicitante e a pessoa
    // dessa área que puxou o projeto. Todas NULLABLE de propósito — nascem
    // opcionais, e os 44 cadastros existentes não têm como respondê-las
    // retroativamente. Não confundir com `team`/`owner`, que são quem EXECUTA
    // (Desenvolvimento|Dados) e quem MANTÉM tecnicamente.
    //
    // `projetoDias` + `projetoHorasDia` são a calculadora de esforço: o total
    // de horas é DERIVADO (dias × horas/dia) e por isso não vira coluna —
    // valor calculado que se armazena é valor que um dia discorda das parcelas.
    projetoDias: integer('projeto_dias'),
    // `mode: 'number'` senão a coluna numeric volta como STRING do driver (o
    // padrão do Drizzle, que protege precisão em dinheiro) e a multiplicação da
    // calculadora viraria concatenação. precision 4/scale 2 = até 99,99 h/dia,
    // folga suficiente para um campo cujo teto real é 24.
    projetoHorasDia: numeric('projeto_horas_dia', { precision: 4, scale: 2, mode: 'number' }),
    projetoRetorno: text('projeto_retorno'),
    // Nome do setor vindo do espelho do TOTVS (GET /api/totvs/sectors), igual
    // às ACLs: snapshot lógico, sem FK — o espelho é recriado a cada sync.
    projetoArea: text('projeto_area'),
    // Pessoa da área que pediu, escolhida na base de colaboradores.
    projetoDono: text('projeto_dono'),
    // ── Acessos (coletados do Clerk — services/acessos-clerk.ts) ─────────────
    // Bloco 100% AUTOMÁTICO: quem escreve é o coletor, nunca o formulário. As
    // cinco colunas ficam FORA de `applicationCreateSchema`/`Update` (ver o
    // `.omit()` lá embaixo) — um POST/PATCH que as mande é simplesmente
    // ignorado, como acontece com `favorite`. Todas NULLABLE e sem default:
    // NULL nas cinco significa "nunca coletado / aplicação sem Clerk", e a
    // tela mostra "—". Um default 0 afirmaria "ninguém acessou", que é mentira
    // para a maioria das fichas (só as que têm chave em MASTERAPPS_CLERK_KEYS
    // são medidas). Mesma distinção que o bloco "Projeto" faz questão de manter.
    //
    // `acessosMes` = sessões (logins) criadas no Clerk da aplicação nos últimos
    // 30 dias. `acessosUsuariosMes` = usuários DISTINTOS ativos no mesmo
    // período (`GET /v1/users/count?last_active_at_since`). São dois números
    // porque respondem perguntas diferentes: "quanto se usa" × "quantos usam".
    acessosMes: integer('acessos_mes'),
    acessosUsuariosMes: integer('acessos_usuarios_mes'),
    // `acessosDiasMes` = dias ÚTEIS distintos do mês-calendário corrente (fuso
    // de Recife, seg–sex) com pelo menos uma sessão criada. É o número que vai
    // para a apresentação, como "12/22": em quantos dias úteis alguém entrou.
    acessosDiasMes: integer('acessos_dias_mes'),
    // Quando a coleta rodou — é o que o scheduler compara para decidir se já
    // passou 24h, e o que a tela mostra como "coletado em".
    acessosColetadoEm: timestamp('acessos_coletado_em', { withTimezone: true }),
    // Último erro da coleta desta aplicação (chave inválida, Clerk fora,
    // contagem parcial por teto de páginas). `null` quando deu certo. Fica na
    // linha, e não só no log, para o admin ver na tela POR QUE está "—".
    acessosErro: text('acessos_erro'),
    deployedAt: text('deployed_at'),
    lastUpdate: text('last_update').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index('applications_slug_idx').on(t.slug),
    index('applications_type_idx').on(t.type),
    index('applications_visibility_idx').on(t.visibility),
    index('applications_status_idx').on(t.status),
  ],
);

export type Application = typeof applications.$inferSelect;
export type NewApplication = typeof applications.$inferInsert;

// Schemas Zod gerados a partir do schema Drizzle (drizzle-zod, stable 0.8.x).
export const applicationSelectSchema = createSelectSchema(applications);

/**
 * Só http(s) entra como link: `z.string().url()` aceita QUALQUER esquema
 * (`javascript:alert(1)`, `data:text/html,…`) e a tela de detalhe usa o valor
 * direto como `href` — um admin (ou um POST direto na API, que ignora a
 * validação de UI do formulário) gravaria XSS armazenado clicável. O schema
 * compartilhado é a validação DO SERVIDOR, então é aqui que a porta fecha.
 */
const httpUrl = (message: string) => (s: z.ZodString) =>
  s.url(message).refine((v) => /^https?:\/\//i.test(v), message);

/**
 * Links de documentação (`requirements`, `docsTechnical`, `docsBusiness`,
 * `docsApi`, `repo`): a tela de detalhe usa o valor direto como `href`, então
 * texto livre não pode entrar. São colunas nullable e o formulário manda `null`
 * quando o campo fica vazio — aceitamos também `''` (mesmo significado de "não
 * cadastrado") para não invalidar payloads/registros antigos.
 */
const docLink = (message: string) => (s: z.ZodString) =>
  httpUrl(message)(s).or(z.literal('')).nullish();

/**
 * Campo de texto do bloco "Projeto": apara espaço, limita tamanho e — o ponto
 * que justifica a função — normaliza `''` para `null`.
 *
 * Sem a normalização, "não informado" teria DUAS representações no banco (NULL
 * e string vazia) e qualquer contagem de preenchimento ("quantos projetos
 * declararam retorno?") teria de lembrar das duas. O formulário já manda `null`
 * quando o campo fica em branco; isto fecha o caminho do POST direto na API,
 * que não passa pela tela. Mesma decisão do `imageKey`, pelo mesmo motivo.
 *
 * O `.trim()` roda ANTES do `.max()` (Zod v4), então 2000 caracteres cercados
 * de espaço passam — o limite vale para o conteúdo, não para o que foi digitado.
 */
const textoDeProjeto = (s: z.ZodString, limite: number, message: string) =>
  s
    .trim()
    .max(limite, message)
    .nullish()
    .transform((v) => (v === '' ? null : v));

export const applicationInsertSchema = createInsertSchema(applications, {
  name: (s) => s.min(2, 'Nome muito curto'),
  slug: (s) => s.regex(/^[a-z0-9-]+$/, 'Slug deve ser kebab-case'),
  description: (s) => s.min(5, 'Descrição muito curta'),
  // A obrigatoriedade da URL de produção é POR SITUAÇÃO (missingStatusUrl):
  // aqui só o formato é validado, como nas irmãs de homolog/dev.
  url: (s) => httpUrl('URL de produção inválida')(s).nullish(),
  urlHml: (s) => httpUrl('URL de homologação inválida')(s).nullish(),
  urlDev: (s) => httpUrl('URL de desenvolvimento inválida')(s).nullish(),
  requirements: docLink('Link do levantamento de requisitos inválido'),
  docsTechnical: docLink('Link da documentação técnica inválido'),
  docsBusiness: docLink('Link da regra de negócio inválido'),
  docsApi: docLink('Link da documentação de API inválido'),
  repo: docLink('Link do repositório inválido'),
  // Colunas notNull: sem `.min()` a string vazia passava pelo Zod E pelo
  // notNull do Postgres — app publicado sem responsável nem time (auditoria).
  // `.trim()` fecha o caso "só espaços" e normaliza o que vai para o banco.
  owner: (s) => s.trim().min(2, 'Informe o responsável técnico'),
  team: (s) => s.trim().min(2, 'Informe o time responsável'),
  /**
   * Bloco "Projeto" — tudo opcional (`.nullish()`), mas com TETO: são campos de
   * texto livre gravados por admin e devolvidos em toda listagem do catálogo;
   * sem `.max()` um POST direto na API guardaria megabytes por linha. O limite
   * é generoso (um parágrafo longo cabe) e existe só para não virar depósito.
   *
   * Os dois primeiros são a calculadora de esforço. Os limites não são
   * decorativos: hora por dia acima de 24 é erro de digitação, não jornada, e
   * dia negativo não existe — barrar aqui evita um total de horas absurdo na
   * tela, já que o total é multiplicação e não tem validação própria.
   */
  projetoDias: (s) =>
    s.min(0, 'Dias não pode ser negativo').max(3650, 'Máximo de 3650 dias').nullish(),
  projetoHorasDia: (s) =>
    s.min(0, 'Horas por dia não pode ser negativo').max(24, 'No máximo 24 horas por dia').nullish(),
  projetoRetorno: (s) => textoDeProjeto(s, 2000, 'Retorno muito longo (máx. 2000)'),
  projetoArea: (s) => textoDeProjeto(s, 200, 'Área muito longa (máx. 200)'),
  projetoDono: (s) => textoDeProjeto(s, 200, 'Nome muito longo (máx. 200)'),
  // Formato explícito: a coluna é text e sem o regex qualquer string passava —
  // o formulário manda YYYY-MM-DD e é esse pattern que o OpenAPI publica para
  // clientes de máquina (a data é exibida com formatDateBR, que espera ISO).
  lastUpdate: (s) =>
    s.regex(/^\d{4}-\d{2}-\d{2}$/, 'Data da última atualização no formato YYYY-MM-DD'),
  /**
   * Só a chave que a PRÓPRIA API gerou (`applications/<uuid>.<ext>`) entra na
   * coluna — o registro não aceita caminho arbitrário do bucket nem URL
   * absoluta. `''` (formulário com o campo limpo) vira `null`, o mesmo que
   * "sem imagem"; `null`/ausente continuam válidos.
   */
  imageKey: () =>
    applicationImageKeySchema
      .or(z.literal(''))
      .nullish()
      .transform((v) => (v === '' ? null : v)),
  /**
   * Telas adicionais: cada item passa pelo MESMO regex estrito da capa — é a
   * defesa contra path traversal, e ela não pode valer só para a primeira
   * imagem. O teto é `APPLICATION_MAX_IMAGES - 1` porque a capa ocupa uma vaga
   * do conjunto.
   *
   * Duplicata é recusada em vez de deduplicada em silêncio: a mesma tela duas
   * vezes no carrossel é engano de quem cadastrou, e corrigir sem avisar
   * esconde o engano em vez de resolvê-lo.
   */
  galeriaKeys: () =>
    z
      .array(applicationImageKeySchema)
      .max(
        APPLICATION_MAX_IMAGES - 1,
        `No máximo ${APPLICATION_MAX_IMAGES} imagens por aplicação, contando a capa`,
      )
      .refine((lista) => new Set(lista).size === lista.length, 'Imagem repetida na galeria')
      .optional(),
});

// ── Controle de acesso por unidade / setor / colaborador ─────────────────────
/**
 * Objeto `access` das aplicações (presente em cada GET; opcional no POST/PATCH).
 * As listas guardam apenas as REFERÊNCIAS LÓGICAS do TOTVS — `codFilial`,
 * `setorNome` e `matricula`. Não há FK para `totvs_collaborators` porque o
 * espelho é apagado/recriado a cada sync; o nome é gravado como snapshot nas
 * tabelas de associação (schemas/application-access.ts) só para exibição.
 *
 * `units` usa o CÓDIGO da filial (não o nome): é o que define acesso no TOTVS e
 * o mesmo código pode aparecer com mais de uma razão social (ex.: código `2` =
 * "MASTERBOI LTDA - FILIAL BR" e "BEZERRA & SOBRAL").
 */
export const applicationAccessSchema = z
  .object({
    units: z.array(z.string().trim().min(1)).max(100).default([]),
    sectors: z.array(z.string().trim().min(1)).max(500).default([]),
    collaborators: z.array(z.string().trim().min(1)).max(5000).default([]),
    restrictedByCollaborator: z.boolean().default(false),
  })
  // Toggle ligado sem ninguém selecionado deixaria a app sem acesso nenhum (ou,
  // pior, "restrita" só no rótulo) — barra no Zod, antes de tocar no banco.
  .refine((a) => !a.restrictedByCollaborator || a.collaborators.length > 0, {
    message: 'Selecione ao menos um colaborador ou desligue a restrição por colaborador',
    path: ['collaborators'],
  });

export type ApplicationAccess = z.infer<typeof applicationAccessSchema>;
/** Forma aceita na entrada (todos os campos opcionais, com default vazio). */
export type ApplicationAccessInput = z.input<typeof applicationAccessSchema>;

export const emptyApplicationAccess = (): ApplicationAccess => ({
  units: [],
  sectors: [],
  collaborators: [],
  restrictedByCollaborator: false,
});

const uniqueTrimmed = (values: readonly string[]): string[] => [
  ...new Set(values.map((v) => v.trim()).filter(Boolean)),
];

/**
 * Aplica as regras de negócio do acesso ANTES de persistir:
 *  - `publico` = qualquer usuário tem acesso → nenhuma restrição é guardada (nem o
 *    toggle), para o filtro de leitura nunca ficar ambíguo;
 *  - toggle de colaborador desligado → a lista de matrículas não restringe
 *    nada e por isso não é guardada;
 *  - dedup + trim de tudo (o unique é (application_id, valor)).
 */
export function normalizeApplicationAccess(
  visibility: Application['visibility'],
  access: ApplicationAccess,
): ApplicationAccess {
  if (visibility === 'publico') return emptyApplicationAccess();
  const collaborators = access.restrictedByCollaborator ? uniqueTrimmed(access.collaborators) : [];
  return {
    units: uniqueTrimmed(access.units),
    sectors: uniqueTrimmed(access.sectors),
    collaborators,
    restrictedByCollaborator: collaborators.length > 0 && access.restrictedByCollaborator,
  };
}

// Payload de criação/edição vindo do formulário (omite campos gerados).
// `restrictedByCollaborator` sai do nível raiz: o único caminho para escrevê-lo
// é `access.restrictedByCollaborator` (uma fonte da verdade, sem divergência
// entre a coluna e as associações).
// `favorite` sai porque virou preferência POR USUÁRIO (`user_favorites`): o
// único caminho é PUT/DELETE /api/me/favorites/:applicationId. Payload antigo
// que ainda mande o campo não dá 400 — o Zod simplesmente o descarta.
// O bloco `acessos*` sai INTEIRO: é escrito só pelo coletor do Clerk
// (services/acessos-clerk.ts). Aceitá-lo no payload deixaria um admin — ou um
// POST direto — inventar número de acesso numa ficha; e um PATCH parcial com
// `acessosMes: null` apagaria a coleta em silêncio.
export const applicationCreateSchema = applicationInsertSchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    restrictedByCollaborator: true,
    favorite: true,
    acessosMes: true,
    acessosUsuariosMes: true,
    acessosDiasMes: true,
    acessosColetadoEm: true,
    acessosErro: true,
  })
  .extend({ access: applicationAccessSchema.optional() });
export const applicationUpdateSchema = applicationCreateSchema.partial();

export type ApplicationCreateInput = z.infer<typeof applicationCreateSchema>;
export type ApplicationUpdateInput = z.infer<typeof applicationUpdateSchema>;

/**
 * Regra das URLs por SITUAÇÃO — a fonte única, usada pela API (POST direto e
 * PATCH contra o estado RESULTANTE da mesclagem, já que um PATCH pode trocar só
 * o status) e pelo formulário de cadastro:
 *  - `prod`    → `url` (produção) obrigatória;
 *  - `homolog` → `urlHml` obrigatória;
 *  - `dev`, `manutencao`, `depreciado`, `pausado` → nenhuma URL exigida (a
 *    aplicação pode nem estar no ar); o formato de qualquer uma preenchida
 *    continua validado pelo schema;
 *  - `type === 'rpa'` → nenhuma URL exigida em situação NENHUMA: robô roda
 *    agendado num servidor, sem tela para abrir — exigir URL obrigaria a
 *    inventar um link, e link falso engana quem lê o inventário.
 *
 * `pausado` NÃO exige URL nenhuma, pelo mesmo motivo de `dev`: é projeto que
 * começou e parou, e a maioria para ANTES de subir em qualquer ambiente —
 * exigir URL obrigaria a inventar um link morto só para salvar a ficha, que é
 * pior do que campo vazio (link falso engana quem lê o inventário, campo vazio
 * não). Exigir `url` seria ainda mais errado: afirmaria que o pausado está no
 * ar. Quem pausou DEPOIS de publicar mantém a URL que já tinha — o campo
 * continua aceito e validado, só não é obrigatório.
 * Devolve o campo faltante (+ mensagem pronta) ou `null` quando está tudo certo.
 * Fica FORA do Zod de propósito: `applicationUpdateSchema.partial()` não
 * carregaria um `superRefine`, e a regra do PATCH precisa dos valores efetivos
 * (payload ?? linha atual), que só a rota conhece.
 */
/**
 * Repositório é OBRIGATÓRIO, exceto em Power Apps.
 *
 * Mesma família de `missingStatusUrl`: é regra CROSS-FIELD (depende do `type`),
 * então não cabe no Zod da coluna — `applicationUpdateSchema` é `.partial()` e
 * não carrega `superRefine`, e o PATCH precisa avaliar o estado RESULTANTE
 * (valor do payload quando veio, senão o da linha atual). O formulário aplica a
 * mesma regra, mas um POST/PATCH direto na API não pode passar por fora.
 *
 * Power Apps fica de fora porque não tem repositório Git: o app vive no
 * Dataverse/Power Platform, não no GitHub.
 */
export function missingRepo(app: {
  type?: Application['type'] | null;
  repo?: string | null;
}): { campo: 'repo'; mensagem: string } | null {
  if (app.type === 'power-apps') return null;
  const vazio = !app.repo || app.repo.trim() === '';
  if (!vazio) return null;
  return {
    campo: 'repo',
    mensagem: 'Link do repositório é obrigatório (exceto Power Apps)',
  };
}

/**
 * Total de horas do projeto — a conta da calculadora de esforço do cadastro:
 * `projetoDias` × `projetoHorasDia`.
 *
 * Vive aqui, e não em cada tela, porque é a MESMA conta no formulário (prévia
 * enquanto se digita) e na ficha da aplicação. Duas implementações da mesma
 * multiplicação divergem no arredondamento e ninguém percebe.
 *
 * `null` quando QUALQUER uma das parcelas falta: meio dado não vira total
 * (5 dias × ? = ?), e devolver 0 afirmaria "esforço nenhum" onde a verdade é
 * "não informado" — a distinção que o bloco inteiro faz questão de manter.
 *
 * O arredondamento em duas casas não é cosmético: em ponto flutuante
 * 13 × 7,7 dá 100,10000000000001 e 3 × 6,6 dá 19,799999999999997 — números que
 * apareceriam crus na tela. (Nem toda multiplicação erra: 3 × 7,7 dá 23,1
 * exato. O arredondamento existe pelas que erram, não por todas.)
 */
export function horasDeProjeto(app: {
  projetoDias?: number | null;
  projetoHorasDia?: number | null;
}): number | null {
  const { projetoDias: dias, projetoHorasDia: horas } = app;
  if (typeof dias !== 'number' || typeof horas !== 'number') return null;
  if (!Number.isFinite(dias) || !Number.isFinite(horas)) return null;
  return Math.round(dias * horas * 100) / 100;
}

export function missingStatusUrl(app: {
  status?: Application['status'] | null;
  type?: Application['type'] | null;
  url?: string | null;
  urlHml?: string | null;
}): { campo: 'url' | 'urlHml'; mensagem: string } | null {
  // RPA não tem URL para exigir (ver o cabeçalho): sai antes de olhar a situação.
  if (app.type === 'rpa') return null;
  // Payload sem status usa o default da coluna ('prod') — mesmo valor que o
  // banco gravaria.
  const status = app.status ?? 'prod';
  const vazio = (v: string | null | undefined) => !v || v.trim() === '';
  if (status === 'prod' && vazio(app.url)) {
    return {
      campo: 'url',
      mensagem: 'URL de produção é obrigatória para aplicações em produção',
    };
  }
  if (status === 'homolog' && vazio(app.urlHml)) {
    return {
      campo: 'urlHml',
      mensagem: 'URL de homologação é obrigatória para aplicações em homologação',
    };
  }
  return null;
}

/**
 * Código de exibição da aplicação a partir da coluna `code`: `APP-007`.
 * Zero à esquerda até 3 dígitos (catálogo com dezenas de apps alinha em
 * colunas); da milésima em diante o número simplesmente cresce (`APP-1024`).
 */
export function formatAppCode(code: number): string {
  return `APP-${String(code).padStart(3, '0')}`;
}
